/**
 * user-presets.ts - localStorage-backed user tone preset store.
 *
 * Storage key: 'amitone-tone-presets'
 * Storage shape: UserPreset[]
 *
 * A UserPreset carries:
 *   - id: stable UUID (generated on save)
 *   - name: user-given display name
 *   - patch: Partial<DemoState> snapshot of PRESET_CONTROLLED_KEYS fields
 *
 * Export file shape (schema version 1):
 *   { schemaVersion: 1, name: string, patch: Partial<DemoState> }
 */

import type { Asset, AssetId, DemoState, MethodSnapshot } from '../state';
import {
  encodePresetBundle,
  encodePresetBundleArchive,
  type EmbeddedAssetInput,
} from '../persistence/bundles';
import { PRESET_CONTROLLED_KEY_LIST, PRESET_CONTROLLED_KEYS } from './tone-presets';

// ── Types ────────────────────────────────────────────────────────────────────

export type UserPreset = {
  id: string;
  name: string;
  patch: Partial<DemoState>;
};

export type PresetExportShape = {
  schemaVersion: 1;
  name: string;
  patch: Partial<DemoState>;
};

// ── Constants ────────────────────────────────────────────────────────────────

const STORAGE_KEY = 'amitone-tone-presets';

// ── localStorage helpers ─────────────────────────────────────────────────────

export function loadUserPresets(): UserPreset[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.flatMap((item) => {
      const preset = normaliseUserPreset(item);
      return preset ? [preset] : [];
    });
  } catch {
    return [];
  }
}

export function saveUserPresets(presets: UserPreset[]): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(presets));
  } catch {
    // Storage quota exceeded or private browsing - silently ignore.
  }
}

function normaliseUserPreset(v: unknown): UserPreset | null {
  if (typeof v !== 'object' || v === null) return null;
  const obj = v as Record<string, unknown>;
  if (typeof obj['id'] !== 'string') return null;
  if (typeof obj['name'] !== 'string' || !obj['name'].trim()) return null;
  const patch = validatePresetPatch(obj['patch']);
  if (patch === null) return null;
  return { id: obj['id'], name: obj['name'].trim(), patch };
}

// ── Snapshot helper ──────────────────────────────────────────────────────────

/**
 * Extract the PRESET_CONTROLLED_KEYS fields from a DemoState into a patch object.
 * This is the canonical set of fields a preset can influence.
 */
export function snapshotPresetPatch(state: DemoState): Partial<DemoState> {
  const patch: Partial<DemoState> = {};
  for (const key of PRESET_CONTROLLED_KEYS) {
    (patch as Record<string, unknown>)[key] = (state as unknown as Record<string, unknown>)[key];
  }
  return patch;
}

// ── Import validation ────────────────────────────────────────────────────────

/**
 * Validate a raw JSON object as a PresetExportShape.
 * Returns the validated patch or null if invalid.
 */
export function validateImportedPreset(raw: unknown): { name: string; patch: Partial<DemoState> } | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const obj = raw as Record<string, unknown>;
  const envelopeKeys = Object.keys(obj);
  const allowedEnvelopeKeys = new Set(['schemaVersion', 'name', 'patch']);
  if (envelopeKeys.some((key) => !allowedEnvelopeKeys.has(key))) return null;

  if (obj['schemaVersion'] !== 1) return null;
  if (typeof obj['name'] !== 'string' || !obj['name'].trim()) return null;
  if (typeof obj['patch'] !== 'object' || obj['patch'] === null) return null;

  const patch = validatePresetPatch(obj['patch']);
  if (patch === null) return null;

  return { name: (obj['name'] as string).trim(), patch };
}

function orderedPresetPatch(patch: Partial<DemoState>): Partial<DemoState> {
  const out: Partial<DemoState> = {};
  const source = patch as Record<string, unknown>;
  const target = out as Record<string, unknown>;
  // The v0.2.5.0 audit locks deterministic patch order to the canonical
  // preset control list so related tone fields stay grouped in shared JSON.
  for (const key of PRESET_CONTROLLED_KEY_LIST) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      target[key] = source[key];
    }
  }
  return out;
}

export function buildPresetExportShape(preset: Pick<UserPreset, 'name' | 'patch'>): PresetExportShape {
  return {
    schemaVersion: 1,
    name: preset.name,
    patch: orderedPresetPatch(preset.patch),
  };
}

export function serializePresetExport(
  preset: Pick<UserPreset, 'name' | 'patch'>,
  pretty = false,
): string {
  return JSON.stringify(buildPresetExportShape(preset), null, pretty ? 2 : undefined);
}

export function methodSnapshotToPresetPatch(snapshot: MethodSnapshot): Partial<DemoState> {
  switch (snapshot.family) {
    case 'A':
      return {
        family: 'A',
        cellSize: snapshot.cellSize,
        stampType: snapshot.stampType,
        stampCoverage: snapshot.stampCoverage,
        polarity: snapshot.polarity,
        density: snapshot.density,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
        gridAngleDeg: snapshot.gridAngleDeg,
        shapeAngleDeg: snapshot.shapeAngleDeg,
        shapeFlipX: snapshot.shapeFlipX ?? false,
        shapeFlipY: snapshot.shapeFlipY ?? false,
        algorithmJitter: snapshot.algorithmJitter,
        patternNoiseScale: snapshot.patternNoiseScale ?? 1,
      };
    case 'B':
      return {
        family: 'B',
        isGrayscale: snapshot.isGrayscale,
        blurRadius: snapshot.blurRadius,
        patternBrightness: snapshot.patternBrightness,
        patternContrast: snapshot.patternContrast,
        polarity: snapshot.polarity,
        density: snapshot.density,
        patternBlendMode: snapshot.blendMode,
        patternScale: snapshot.patternScale,
        patternRotationDeg: snapshot.patternRotationDeg ?? 0,
        patternFlipX: snapshot.patternFlipX ?? false,
        patternFlipY: snapshot.patternFlipY ?? false,
        patternNoise: snapshot.patternNoise,
        patternNoiseScale: snapshot.patternNoiseScale,
        patternHalftoneEnabled: snapshot.patternHalftoneEnabled ?? false,
        patternHalftoneCellSize: snapshot.patternHalftoneCellSize ?? 8,
        patternHalftoneStampType: snapshot.patternHalftoneStampType ?? 'circle',
        patternHalftoneStampCoverage: snapshot.patternHalftoneStampCoverage ?? 0.9,
        patternHalftonePolarity: snapshot.patternHalftonePolarity ?? 'black',
        patternHalftoneDensity: snapshot.patternHalftoneDensity ?? 50,
        patternHalftoneBlackThreshold: snapshot.patternHalftoneBlackThreshold ?? 0,
        patternHalftoneWhiteThreshold: snapshot.patternHalftoneWhiteThreshold ?? 1,
        patternHalftoneGridAngleDeg: snapshot.patternHalftoneGridAngleDeg ?? 0,
        patternHalftoneShapeAngleDeg: snapshot.patternHalftoneShapeAngleDeg ?? 0,
        patternHalftoneShapeFlipX: snapshot.patternHalftoneShapeFlipX ?? false,
        patternHalftoneShapeFlipY: snapshot.patternHalftoneShapeFlipY ?? false,
        patternHalftoneAlgorithmJitter: snapshot.patternHalftoneAlgorithmJitter ?? 0.075,
        patternHalftoneNoiseScale: snapshot.patternHalftoneNoiseScale ?? snapshot.patternNoiseScale,
        patternInvert: snapshot.patternInvert,
        patternOpacity: snapshot.patternOpacity,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
      };
    case 'C': {
      const common: Partial<DemoState> = {
        family: 'C',
        algorithmA: snapshot.algorithm,
        cellSize: snapshot.cellSize,
        stampType: snapshot.stampType,
        stampCoverage: snapshot.stampCoverage,
        polarity: snapshot.polarity,
        density: snapshot.density,
        blackThreshold: snapshot.blackThreshold,
        whiteThreshold: snapshot.whiteThreshold,
        gridAngleDeg: snapshot.gridAngleDeg,
        shapeAngleDeg: snapshot.shapeAngleDeg,
        shapeFlipX: snapshot.shapeFlipX ?? false,
        shapeFlipY: snapshot.shapeFlipY ?? false,
        algorithmJitter: snapshot.algorithmJitter,
        grainStrength: snapshot.grainStrength ?? 0,
        grainScale: snapshot.grainScale ?? 4,
      };
      switch (snapshot.algorithm) {
        case 'bayer':
          return { ...common, matrixSize: snapshot.matrixSize };
        case 'bluenoise':
          return common;
        case 'floyd-steinberg':
          return { ...common, serpentine: snapshot.serpentine, fsStrength: snapshot.strength };
        case 'atkinson':
          return { ...common, atkinsonStrength: snapshot.strength };
        case 'werness':
          return { ...common, brightness: snapshot.brightness, contrast: snapshot.contrast };
      }
    }
  }
}

export function assetIdsForMethodSnapshot(snapshot: MethodSnapshot): AssetId[] {
  const ids: AssetId[] = [];
  if (snapshot.patternRefId !== null) ids.push(snapshot.patternRefId);
  if (snapshot.stampMaskRefId !== null) ids.push(snapshot.stampMaskRefId);
  return ids;
}

export function embeddedAssetsForMethodSnapshot(
  state: Pick<DemoState, 'assetLibrary' | 'assetData'>,
  snapshot: MethodSnapshot,
): EmbeddedAssetInput[] {
  const ids = new Set(assetIdsForMethodSnapshot(snapshot));
  const assets: EmbeddedAssetInput[] = [];
  for (const asset of state.assetLibrary) {
    if (!ids.has(asset.id)) continue;
    const image = state.assetData.get(asset.id);
    if (image === undefined) continue;
    assets.push({ asset, image });
  }
  return assets;
}

export function mergeEmbeddedAssetsIntoState(
  state: Pick<DemoState, 'assetLibrary' | 'assetData' | 'assetDataAccessOrder'>,
  assets: readonly Asset[],
  assetData: ReadonlyMap<string, ImageData>,
): Pick<DemoState, 'assetLibrary' | 'assetData' | 'assetDataAccessOrder'> {
  const nextLibrary = state.assetLibrary.slice();
  const known = new Set(nextLibrary.map((asset) => asset.id));
  const nextData = new Map(state.assetData);
  const nextOrder = state.assetDataAccessOrder.slice();
  for (const asset of assets) {
    if (!known.has(asset.id)) {
      nextLibrary.push(asset);
      known.add(asset.id);
    }
    const image = assetData.get(asset.id);
    if (image !== undefined) {
      nextData.set(asset.id, image);
      const existingIndex = nextOrder.indexOf(asset.id);
      if (existingIndex !== -1) nextOrder.splice(existingIndex, 1);
      nextOrder.push(asset.id);
    }
  }
  return {
    assetLibrary: nextLibrary,
    assetData: nextData,
    assetDataAccessOrder: nextOrder,
  };
}

export function binaryPatchForMethodSnapshot(
  snapshot: MethodSnapshot,
  assetData: ReadonlyMap<string, ImageData>,
): Partial<DemoState> {
  const patch: Partial<DemoState> = {};
  if (snapshot.family === 'B' && snapshot.patternRefId !== null) {
    const pattern = assetData.get(snapshot.patternRefId);
    if (pattern !== undefined) patch.patternImage = pattern;
  }
  if (snapshot.stampMaskRefId !== null) {
    const stampMask = assetData.get(snapshot.stampMaskRefId);
    if (stampMask !== undefined) patch.customStampMask = stampMask;
  }
  return patch;
}

export function serializePresetBundleExport(
  input: {
    name: string;
    preset: MethodSnapshot;
    assets: readonly EmbeddedAssetInput[];
  },
): string {
  return encodePresetBundle(input);
}

export function serializePresetBundleArchive(
  input: {
    name: string;
    preset: MethodSnapshot;
    assets: readonly EmbeddedAssetInput[];
  },
): Uint8Array {
  return encodePresetBundleArchive(input);
}

function validatePresetPatch(raw: unknown): Partial<DemoState> | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) return null;
  const patch = raw as Record<string, unknown>;
  const out: Record<string, unknown> = {};

  for (const key of Object.keys(patch)) {
    if (key === 'halftonePrepass') continue;
    if (!PRESET_CONTROLLED_KEYS.has(key as keyof DemoState)) return null;
    const value = patch[key];
    if (!isValidPresetValue(key as keyof DemoState, value)) return null;
    out[key] = value;
  }

  return out as Partial<DemoState>;
}

function finiteNumberInRange(value: unknown, min: number, max: number): value is number {
  return typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
}

function oneOf<T extends string | number>(
  value: unknown,
  allowed: readonly T[],
): value is T {
  return allowed.includes(value as T);
}

/**
 * Per-key validators for every PRESET_CONTROLLED_KEYS entry accepted by imports.
 */
function isValidPresetValue(key: keyof DemoState, value: unknown): boolean {
  switch (key) {
    case 'family':
      return oneOf(value, ['A', 'B', 'C'] as const);
    case 'algorithmA':
      return oneOf(value, ['bayer', 'bluenoise', 'floyd-steinberg', 'atkinson', 'werness'] as const);
    case 'stampType':
      return oneOf(value, [
        'pixel',
        'circle',
        'diamond',
        'line',
        'cross',
        'ellipse',
        'noise',
        'asterisk',
        'flower',
        'star',
        'heart',
        'spade',
        'club',
        'custom',
      ] as const);
    case 'polarity':
      return oneOf(value, ['black', 'white'] as const);
    case 'patternBlendMode':
      return oneOf(value, ['threshold', 'multiply', 'screen', 'overlay', 'softlight', 'pinlight'] as const);
    case 'matrixSize':
      return oneOf(value, [2, 4, 8, 16] as const);
    case 'serpentine':
    case 'isGrayscale':
    case 'shapeFlipX':
    case 'shapeFlipY':
    case 'patternFlipX':
    case 'patternFlipY':
    case 'patternHalftoneShapeFlipX':
    case 'patternHalftoneShapeFlipY':
    case 'patternHalftoneEnabled':
    case 'patternInvert':
      return typeof value === 'boolean';
    case 'patternHalftoneStampType':
      return oneOf(value, [
        'pixel',
        'circle',
        'diamond',
        'line',
        'cross',
        'ellipse',
        'noise',
        'asterisk',
        'flower',
        'star',
        'heart',
        'spade',
        'club',
        'custom',
      ] as const);
    case 'patternHalftonePolarity':
      return oneOf(value, ['black', 'white'] as const);
    case 'cellSize':
    case 'patternHalftoneCellSize':
      return finiteNumberInRange(value, 1, 32);
    case 'stampCoverage':
    case 'patternHalftoneStampCoverage':
      return finiteNumberInRange(value, 0, 1.5);
    case 'density':
    case 'patternHalftoneDensity':
      return finiteNumberInRange(value, 0, 100);
    case 'blackThreshold':
    case 'whiteThreshold':
    case 'algorithmJitter':
    case 'grainStrength':
    case 'fsStrength':
    case 'atkinsonStrength':
    case 'patternNoise':
    case 'patternOpacity':
    case 'patternHalftoneBlackThreshold':
    case 'patternHalftoneWhiteThreshold':
    case 'patternHalftoneAlgorithmJitter':
      return finiteNumberInRange(value, 0, 1);
    case 'gridAngleDeg':
    case 'shapeAngleDeg':
    case 'patternHalftoneGridAngleDeg':
    case 'patternHalftoneShapeAngleDeg':
      return finiteNumberInRange(value, 0, 180);
    case 'patternRotationDeg':
      return finiteNumberInRange(value, 0, 360);
    case 'brightness':
    case 'contrast':
      return finiteNumberInRange(value, -50, 50);
    case 'patternBrightness':
    case 'patternContrast':
      return finiteNumberInRange(value, -100, 100);
    case 'blurRadius':
      return finiteNumberInRange(value, 0, 16);
    case 'patternScale':
      return finiteNumberInRange(value, 0.01, 4);
    case 'patternNoiseScale':
    case 'patternHalftoneNoiseScale':
    case 'grainScale':
      return finiteNumberInRange(value, 1, 16);
    default:
      return false;
  }
}

// ── UUID generator ───────────────────────────────────────────────────────────

export function generateId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for environments without crypto.randomUUID.
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

// ── Export download ──────────────────────────────────────────────────────────

export function downloadPresetAsJson(preset: UserPreset): void {
  const blob = new Blob([serializePresetExport(preset)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${preset.name.replace(/[^a-z0-9_-]/gi, '_')}.amitone-preset.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  // Revoke the object URL shortly after to free memory.
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}

export function downloadPresetAsBundle(input: {
  name: string;
  preset: MethodSnapshot;
  assets: readonly EmbeddedAssetInput[];
}): void {
  const archive = serializePresetBundleArchive(input);
  const blob = new Blob([new Uint8Array(archive)], { type: 'application/zip' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `${input.name.replace(/[^a-z0-9_-]/gi, '_')}.amtp`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
